/* Webinars -> Campaigns -> Readiness / preflight panel (2026-09 round 7).
 *
 * One honest summary of every dependency a campaign needs before it can run
 * for real - master switch, TEST/LIVE mode, provider capability + credential
 * PRESENCE, provider webinar ID, canonical public HTTPS URL, lead source,
 * approved WhatsApp templates, date/time/timezone, registration method,
 * reminders, attendance ingestion, CTA threshold, voice follow-up and
 * retry/failure handling. Rendered verbatim from
 * GET /webinar/campaigns/{id}/readiness - the same checks the server uses to
 * refuse activating a LIVE campaign, so this panel and the gate cannot
 * disagree. Credential VALUES never reach the browser (presence only).
 */
import { useCallback, useEffect, useState } from "react";
import { api, errDetail } from "@/lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { ClipboardCheck, RefreshCw } from "lucide-react";
import { ReadinessBadge, toneClass } from "@/components/webinar/webinarShared";

const TemplateRows = ({ kinds }) => (
  <div className="overflow-x-auto mt-1" data-testid="readiness-template-kinds">
    <Table>
      <TableHeader>
        <TableRow>
          <TableHead>Message</TableHead><TableHead>Sent by this campaign</TableHead>
          <TableHead>Template kind</TableHead><TableHead>Interakt template</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {kinds.map((k) => (
          <TableRow key={k.message_kind} data-testid={`readiness-template-${k.message_kind}`}>
            <TableCell className="text-[11px]">{k.label}</TableCell>
            <TableCell className="text-[11px]">{k.used_by_campaign ? "Yes" : "No"}</TableCell>
            <TableCell className="text-[11px] font-mono">
              {k.effective_template_kind}{k.overridden ? " (override)" : ""}
            </TableCell>
            <TableCell className="text-[11px]">
              {k.mapped
                ? <span>{k.template_name} <span className="text-muted-foreground">({k.language || "en"})</span></span>
                : <span className={k.used_by_campaign ? "text-[hsl(var(--danger))]" : "text-muted-foreground"}>Not mapped</span>}
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  </div>
);

export const WebinarReadinessPanel = ({ campaignId, refreshKey = 0 }) => {
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    if (!campaignId) return;
    setLoading(true);
    try {
      const r = await api.get(`/webinar/campaigns/${campaignId}/readiness`);
      setData(r.data);
      setError(null);
    } catch (e) { setError(errDetail(e)); } finally { setLoading(false); }
  }, [campaignId]);
  useEffect(() => { load(); }, [load, refreshKey]);

  if (error) {
    return (
      <p className="text-xs text-[hsl(var(--danger))]" data-testid="webinar-readiness-error">
        Could not load readiness: {error}
      </p>
    );
  }
  if (!data) {
    return <p className="text-xs text-muted-foreground" data-testid="webinar-readiness-loading">Checking readiness…</p>;
  }
  const missing = data.live_missing || [];
  return (
    <Card data-testid="webinar-readiness">
      <CardHeader className="pb-2 flex-row items-center justify-between space-y-0 gap-2 flex-wrap">
        <CardTitle className="text-sm font-heading flex items-center gap-2">
          <ClipboardCheck className="h-4 w-4" /> Readiness &amp; preflight
          <Badge variant="outline" data-testid="webinar-readiness-mode"
                 className={`text-[10px] ${toneClass(data.test_mode ? "warning" : "danger")}`}>
            {data.mode}
          </Badge>
        </CardTitle>
        <Button size="sm" variant="ghost" className="h-7 px-2" onClick={load} disabled={loading}
                data-testid="webinar-readiness-refresh" title="Re-check">
          <RefreshCw className={`h-3.5 w-3.5 ${loading ? "animate-spin" : ""}`} />
        </Button>
      </CardHeader>
      <CardContent className="space-y-3 text-xs">
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
          <div className="rounded-md border p-2" data-testid="webinar-readiness-activate">
            <p className="text-[11px] text-muted-foreground">Can activate now ({data.mode})</p>
            <p className={data.can_activate ? "text-[hsl(var(--success))] font-medium" : "text-[hsl(var(--danger))] font-medium"}>
              {data.can_activate ? "Yes" : "No"}
            </p>
            {!data.can_activate && (data.activation_blockers || []).map((b) => (
              <p key={b} className="text-[10px] text-muted-foreground">{b}</p>
            ))}
          </div>
          <div className="rounded-md border p-2" data-testid="webinar-readiness-live">
            <p className="text-[11px] text-muted-foreground">Ready to go LIVE</p>
            <p className={data.ready_for_live ? "text-[hsl(var(--success))] font-medium" : "text-[hsl(var(--warning))] font-medium"}>
              {data.ready_for_live ? "Yes" : `No — ${missing.length} missing`}
            </p>
            <p className="text-[10px] text-muted-foreground">
              A LIVE campaign cannot be activated while any required dependency is missing.
            </p>
          </div>
          <div className="rounded-md border p-2" data-testid="webinar-readiness-timezone">
            <p className="text-[11px] text-muted-foreground">Campaign timezone</p>
            <p className="font-medium font-mono">{data.timezone}</p>
            <p className="text-[10px] text-muted-foreground">Reminder times and displayed times use this zone.</p>
          </div>
        </div>

        {data.public_urls && (
          <div className="rounded-md border p-2 space-y-1" data-testid="webinar-readiness-urls">
            <div className="text-[11px] text-muted-foreground">
              Public join / callback base{" "}
              {data.public_urls.externally_valid
                ? <Badge variant="outline" className={`text-[10px] ${toneClass("success")}`}>https · externally valid</Badge>
                : <Badge variant="outline" className={`text-[10px] ${toneClass("danger")}`}>not valid for LIVE</Badge>}
            </div>
            {data.public_urls.base_url ? (
              <>
                <code className="block text-[11px] break-all bg-muted rounded px-1.5 py-1" data-testid="webinar-readiness-join-base">
                  {data.public_urls.join_base}
                </code>
                {Object.entries(data.public_urls.attendance_webhooks || {}).map(([p, u]) => (
                  <code key={p} className="block text-[10px] break-all text-muted-foreground">{p}: {u}</code>
                ))}
              </>
            ) : (
              <p className="text-[hsl(var(--danger))]" data-testid="webinar-readiness-no-url">
                {(data.public_urls.errors || []).join(" ") || "No public base URL configured."}
              </p>
            )}
          </div>
        )}

        <ul className="divide-y rounded-md border" data-testid="webinar-readiness-checks">
          {(data.checks || []).map((c) => (
            <li key={c.key} className="p-2 space-y-1" data-testid={`readiness-check-${c.key}`}>
              <div className="flex items-center gap-2 flex-wrap">
                <ReadinessBadge status={c.status} testId={`readiness-status-${c.key}`} />
                <span className="font-medium">{c.label}</span>
              </div>
              <p className="text-muted-foreground">{c.detail}</p>
              {c.key === "provider" && c.data && (
                <p className="text-[10px] text-muted-foreground" data-testid="readiness-provider-credentials">
                  Credentials: {c.data.credentials_configured ? "present" : "missing"}
                  {(c.data.missing_credential_keys || []).length > 0 && ` (${c.data.missing_credential_keys.join(", ")})`}
                  {" "}· values are never shown
                </p>
              )}
              {c.key === "template_mapping" && c.data?.kinds && <TemplateRows kinds={c.data.kinds} />}
              {c.key === "source_association" && (c.data?.sources || []).length > 0 && (
                <ul className="list-disc pl-4 text-[11px]">
                  {c.data.sources.map((s) => (
                    <li key={`${s.source_type}:${s.source_id}`}>{s.source_type_label}: {s.label}</li>
                  ))}
                </ul>
              )}
              {c.key === "reminders" && (c.data?.next_session_schedule || []).length > 0 && (
                <ul className="text-[11px] text-muted-foreground">
                  {c.data.next_session_schedule.map((r) => (
                    <li key={r.kind}>{r.kind.replace(/_/g, " ")} — {r.run_at_local}</li>
                  ))}
                </ul>
              )}
            </li>
          ))}
        </ul>
      </CardContent>
    </Card>
  );
};

export default WebinarReadinessPanel;
